<template>
  <div class="d-flex justify-content-end">
    <McTableFilter
      :activeFilters="localFilters"
      :parseFilterDataMappings="parseFilterDataMappings"
      :presetFullObjects="setFullObjects"
      :hasChangeData="hasChangeData"
      @submit="handleSubmit"
      @update:showModal="handleShowModalChange"
      @reset="handleReset"
      @getFullObjects="updateLocalFullObjects"
    >
      <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
        <div class="mc-modal__filter-item">
          <FormulateInput
            v-bind="alertFields"
            :fullObject="fullObjects.alertCode"
            :value="formValues.alertCode"
            @update:formValues="updateFormValues({ key: 'alertCode', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'alertCode', value: $event })"
          />
        </div>

        <div class="mc-modal__filter-item">
          <FormulateInput
            v-bind="vehicleFields"
            :fullObject="fullObjects.licensePlate"
            :value="formValues.licensePlate"
            @update:formValues="updateFormValues({ key: 'licensePlate', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'licensePlate', value: $event })"
          />
        </div>

        <div class="mc-modal__filter-item">
          <FormulateInput
            v-bind="createdOnField"
            :value="formValues.createdOn"
            value-type="YYYY-MM-DD HH:mm:ss"
            @input="updateFormValues({ key: 'createdOn', value: $event })"
          />
        </div>

        <div class="mc-modal__filter-item">
          <FormulateInput
            type="group"
            name="statusCheck"
            :value="formValues.statusCheck"
            label="Status"
          >
            <div class="mc-modal__filter-button-group">
              <template v-for="option in statusOptions">
                <button
                  :key="`${option.key}-status-${btnKey}`"
                  type="button"
                  :class="{ 'mc-modal__filter-button--active': statusCheck[option.key] }"
                  class="mc-modal__filter-button"
                  @click="toggleStatus(option.key)"
                >
                  {{ option.label }}
                </button>
              </template>
            </div>
          </FormulateInput>
        </div>

        <div class="mc-modal__filter-item">
          <FormulateInput
            type="group"
            name="category"
            :value="formValues.category"
            :label="$t('global.field.category')"
          >
            <div class="mc-modal__filter-button-group">
              <template v-for="option in categoryOptions">
                <button
                  :key="`${option.key}-category-${btnKey}`"
                  type="button"
                  :class="{ 'mc-modal__filter-button--active': categoryCheck[option.key] }"
                  class="mc-modal__filter-button"
                  @click="toggleCategory(option.key)"
                >
                  {{ option.label }}
                </button>
              </template>
            </div>
          </FormulateInput>
        </div>
      </template>
    </McTableFilter>
  </div>
</template>
<script>
import McTableFilter from '@/components/McTableFilter'
import { Helper } from '@/mixin/helper'
import Notification from '@/mixin/notification'
import {
  GET_VEHICLE_FILTER,
  GET_VEHICLE_GROUPS,
  GET_VEHICLE_TYPES,
} from '@/services/api/master/fleet'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  components: {
    McTableFilter,
  },
  mixins: [Helper, Notification],
  apollo: {
    vehicleTypes: {
      query: GET_VEHICLE_TYPES,
      variables() {
        return {
          filter: {
            hasVehicle: true,
            isActive: true,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let vehicleTypes = { ...data.vehicleTypes }
        this.$store.commit('report/updateVehicleTypeList', {
          list: vehicleTypes.vehicleTypes.data,
          loaded: true,
        })
      },
    },
    vgroups: {
      query: GET_VEHICLE_GROUPS,
      result({ data, loading }) {
        if (loading) return
        let vgroups = { ...data.vgroups }
        this.$store.commit('report/updateVgroupList', {
          list: vgroups.vgroups,
          loaded: true,
        })
      },
    },
  },
  props: {
    activeFilters: {
      type: Object,
      required: true,
    },
    alertCode: {
      type: Array,
      required: true,
    },
    setFullObjects: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['applyFilters', 'applyFullObjects'],

  data() {
    return {
      btnKey: 0,
      isModalVisible: false,
      localFilters: {},
      statusCheck: {
        active: false,
        inactive: false,
      },
      categoryCheck: {
        urgent: false,
        important: false,
        intensity: false,
      },
      hasChangeData: false,
      vehicleTypes: {
        vehicleTypes: [],
      },
      vgroups: {
        vgroups: [],
      },
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),

    statusOptions() {
      return [
        { key: 'active', label: this.$t('customField.type.active') },
        { key: 'inactive', label: this.$t('customField.type.inactive') },
      ]
    },

    categoryOptions() {
      let options = [
        { key: 'important', label: this.$t('setting.list.notification.form.important') },
        { key: 'intensity', label: this.$t('setting.list.notification.form.intensity') },
      ]

      if (this.hasAccess('e4e-alert-center-urgent:read')) {
        options.unshift({ key: 'urgent', label: this.$t('setting.list.notification.form.urgent') })
      }

      return options
    },

    parseFilterDataMappings() {
      return {
        alertCode: 'alertCode',
        licensePlate: 'vehicleId',
        statusCheck: 'statusCheck',
        createdOn: 'createdOn',
        category: 'category',
      }
    },

    alertFields() {
      return {
        name: 'alertCode',
        isShow: true,
        label: this.$t('notification.setting.emailNotification.type'),
        type: 'report-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('global.object.notification')}`,
        trackBy: 'id',
        displayedBy: 'label',
        listGetter: (filter) => this.getAlertList(filter),
        multiple: true,
        tableFields: [
          {
            label: this.$t('setting.list.notification.form.notificationName'),
            key: 'label',
            isSortable: false,
          },
          {
            label: this.$t('notification.text.notificationCategory'),
            key: 'category',
            isSortable: false,
            formatter: (value) => {
              if (!value || !Array.isArray(value) || value.length === 0) return '-'
              return value.join(', ')
            },
          },
        ],
        filters: [
          {
            label: `${this.$t('global.action.choose')} ${this.$t(
              'global.field.category'
            )} ${this.$t('global.object.notification')}`,
            key: 'alertCategory',
            data: this.getFilterValue(1000, 'alertCategory'),
            model: [],
          },
        ],
      }
    },
    vgroupList() {
      return this.$store.state.report.vGroupList
    },
    vehicleTypeList() {
      return this.$store.state.report.vehicleTypeList
    },
    vehicleFields() {
      const tableFields = [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'licensePlate',
          isSortable: true,
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
      return {
        name: 'licensePlate',
        isShow: true,
        label: this.$t('report.fields.licensePlate'),
        type: 'report-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('report.fields.licensePlate')}`,
        trackBy: 'id',
        displayedBy: 'licensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        multiple: true,
        tableFields: tableFields,
        filters: [
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vgroup',
            data: this.getFilterValue(1000, 'vgroup'),
            model: [],
          },
        ],
      }
    },

    createdOnField() {
      return {
        name: 'createdOn',
        isShow: true,
        label: this.$t('setting.list.notification.form.createdOn'),
        type: 'date-picker',
        placeholder: `${this.$t('global.action.choose')} ${this.$t(
          'setting.list.notification.form.createdOn'
        )}`,
        dateFormat: 'YYYY-MM-DD HH:mm:ss',
        datepickerType: 'datetime',
        range: true,
      }
    },

    alertCategories() {
      return [
        {
          id: 'global.alert.type.adas',
          name: this.$t('global.alert.type.adas'),
          scope: this.hasAccess(['e1g-web-adas:read']),
        },
        {
          id: 'global.alert.type.dms',
          name: this.$t('global.alert.type.dms'),
          scope: this.hasAccess(['e1c-web-dms:read']),
        },
        {
          id: 'global.alert.type.general',
          name: this.$t('global.alert.type.general'),
          scope: true,
        },
        {
          id: 'global.alert.type.driverBehavior',
          name: this.$t('global.alert.type.driverBehavior'),
          scope: true,
        },
        {
          id: 'global.alert.type.fleetTask',
          name: this.$t('global.alert.type.fleetTask'),
          scope: this.hasAccess(['fleet-task-instant:read']),
        },
        {
          id: 'global.alert.type.cdm',
          name: this.$t('global.alert.type.cdm'),
          scope: this.hasAccess(['e1q-brinks-cdm:read']),
        },
        {
          id: 'global.alert.type.bsd',
          name: this.$t('global.alert.type.bsd'),
          scope: this.hasAccess(['e1r-bsd-blind-spot-detection:read']),
        },
      ]
    },
  },

  watch: {
    activeFilters: {
      handler(newValue) {
        if (newValue) {
          this.setLocalFilters(newValue)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    toggleStatus(key) {
      this.statusCheck[key] = !this.statusCheck[key]
      this.$emit('update:formValues', { key: 'statusCheck', value: { ...this.statusCheck } })
      this.hasChangeData = true
    },

    toggleCategory(key) {
      this.categoryCheck[key] = !this.categoryCheck[key]
      this.$emit('update:formValues', { key: 'category', value: { ...this.categoryCheck } })
      this.hasChangeData = true
    },

    setLocalFilters(filters) {
      this.localFilters = cloneDeep(filters)

      const status = filters.statusCheck
      this.statusCheck.active = Array.isArray(status)
        ? status.includes('active')
        : !!(status && status.active)
      this.statusCheck.inactive = Array.isArray(status)
        ? status.includes('inactive')
        : !!(status && status.inactive)

      const category = filters.category || {}
      this.categoryCheck.urgent = !!category.urgent
      this.categoryCheck.important = !!category.important
      this.categoryCheck.intensity = !!category.intensity
    },

    handleSubmit(value) {
      const filters = []

      value.forEach((f) => {
        this.setOrUpdateFilter(filters, f.key, f.values)
      })

      const selectedStatus = {}
      Object.keys(this.statusCheck).forEach((key) => {
        selectedStatus[key] = this.statusCheck[key]
      })
      this.setOrUpdateFilter(filters, 'statusCheck', selectedStatus)

      const selectedCategory = { ...this.categoryCheck }
      this.setOrUpdateFilter(filters, 'category', selectedCategory)

      this.$emit('applyFilters', filters)
      this.hasChangeData = false
    },

    setOrUpdateFilter(filterList, key, newValue) {
      const index = filterList.findIndex((f) => f.key === key)
      if (index !== -1) {
        filterList[index].values = newValue
      } else {
        filterList.push({ key, values: newValue })
      }
    },

    handleReset() {
      this.statusCheck = { active: false, inactive: false }
      this.categoryCheck = { urgent: false, important: false, intensity: false }
      this.$emit('applyFullObjects', {})
    },

    updateLocalFullObjects(value) {
      this.$emit('applyFullObjects', value)
    },

    handleShowModalChange(value) {
      this.isModalVisible = value
    },

    async getVehicleList(opt) {
      const { orderBy, ...otherOpt } = opt
      const self = this
      const response = await this.$apollo.query({
        query: GET_VEHICLE_FILTER,
        variables: {
          filter: {
            status: ['I', 'R', 'A'],
            orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
            ...otherOpt,
          },
        },
      })
      let res = { ...response.data.vehiclesFilter.vehiclesFilterPaginated }
      res.data = res.data.map((item) => {
        let status = ''
        if (item.status === 'I') {
          status = self.$t('master.status.aktif')
        } else if (item.status === 'R') {
          status = self.$t('master.status.perbaikan')
        } else {
          status = self.$t('master.status.arsip')
        }
        return Object.assign({}, item, { statusString: status })
      })
      return res
    },

    getFilterValue(interval, type) {
      return new Promise((resolve) => {
        const { loaded, list } = this.getListFilterLoaded(type)
        if (!loaded) {
          const intervalId = setInterval(() => {
            const { loaded, list } = this.getListFilterLoaded(type)
            if (loaded) {
              clearInterval(intervalId)
              resolve(list)
            }
          }, interval)
        } else {
          resolve(list)
        }
      })
    },

    getListFilterLoaded(type) {
      let loaded = false
      let list = []
      switch (type) {
        case 'vehicleType':
          list = this.vehicleTypeList.list.map((item) => {
            return {
              id: item.vehicleTypeId,
              name: `${item.manufacturer} / ${item.model} / ${item.type}`,
            }
          })
          loaded = this.vehicleTypeList.loaded
          break
        case 'vgroup':
          list = this.vgroupList.list.map((item) => {
            return { id: item.id, name: item.name }
          })
          loaded = this.vgroupList.loaded
          break
        case 'alertCategory': {
          list = this.alertCategories
            .filter((item) => item.scope)
            .map((item) => ({
              ...item,
              name: this.$t(item.name),
            }))
          loaded = true
          break
        }
        default:
          break
      }
      return { loaded, list }
    },
  },
}
</script>
